import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select, SidePanel, type BadgeVariant } from '@silverfalls/sterling';
import { FilterInput } from '../components/FilterInput.js';
import { useToast } from '../components/Toast.js';
import { formatRelativeDate } from '../lib/format.js';
import { sortBy, toggleSort, type SortState } from '../lib/sort.js';
import {
  createPurchaseOrder,
  fetchPurchaseOrders,
  fetchVendors,
  type CreatePurchaseOrderInput,
  type CreatePurchaseOrderLineInput,
  type PricingSource,
  type PurchaseOrderRow,
  type PurchaseOrderState,
  type VendorRow,
} from '../api/p2p.js';

// Sterling density: lead with vendor (operators recognise POs by who they're
// for, not by ref). poRef + currencyCode drop at default density — ref is
// structural (URL has it), currency is ≥80% identical (USD).
type PoSortKey = 'vendor' | 'state' | 'createdAt' | 'subtotal' | 'lines';

function poSortAccessor(po: PurchaseOrderRow, key: PoSortKey): unknown {
  switch (key) {
    case 'vendor':
      return po.vendor?.legalName ?? po.vendorRef;
    case 'state':
      return po.state;
    case 'createdAt':
      return po.createdAt;
    case 'subtotal':
      return Number(po.subtotal);
    case 'lines':
      return po.lines.length;
  }
}

function matchesPoQuery(po: PurchaseOrderRow, q: string): boolean {
  if (q === '') return true;
  const needle = q.toLowerCase();
  return (
    po.poRef.toLowerCase().includes(needle) ||
    po.vendorRef.toLowerCase().includes(needle) ||
    (po.vendor?.legalName.toLowerCase().includes(needle) ?? false) ||
    (po.vendor?.displayName.toLowerCase().includes(needle) ?? false) ||
    po.state.toLowerCase().includes(needle)
  );
}

const REQUESTING_USER = 'u-ui-skeleton';

const PRICING_SOURCES: ReadonlyArray<PricingSource> = [
  'CONTRACT',
  'QUOTE',
  'STANDARD_PRICE_LIST',
  'NEGOTIATED',
];

const PO_STATE_FILTERS: ReadonlyArray<{
  label: string;
  value: PurchaseOrderState | 'ALL' | 'RECEIVABLE';
}> = [
  { label: 'All', value: 'ALL' },
  // Convenience group covering the receivable-but-not-complete states —
  // these are the ones operators most often want to triage.
  { label: 'Receivable', value: 'RECEIVABLE' },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Transmitted', value: 'TRANSMITTED' },
  { label: 'Acknowledged', value: 'ACKNOWLEDGED' },
  { label: 'Open', value: 'OPEN' },
  { label: 'Partially received', value: 'PARTIALLY_RECEIVED' },
  { label: 'Fully received', value: 'FULLY_RECEIVED' },
  { label: 'Closed', value: 'CLOSED' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

const RECEIVABLE_STATES: ReadonlySet<PurchaseOrderState> = new Set<PurchaseOrderState>([
  'OPEN',
  'ACKNOWLEDGED',
  'PARTIALLY_RECEIVED',
]);

type DialogState = { open: false } | { open: true };

interface PurchaseOrdersProps {
  readonly opcoRef: string;
}

export function PurchaseOrders({ opcoRef }: PurchaseOrdersProps): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [pos, setPos] = useState<ReadonlyArray<PurchaseOrderRow>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<PurchaseOrderState | 'ALL' | 'RECEIVABLE'>(
    'ALL',
  );
  const [sort, setSort] = useState<SortState<PoSortKey> | null>({
    key: 'createdAt',
    direction: 'desc',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchPurchaseOrders(opcoRef);
      setPos(rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [opcoRef]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    let filtered = pos.filter((po) => matchesPoQuery(po, query));
    if (stateFilter === 'RECEIVABLE') {
      filtered = filtered.filter((po) => RECEIVABLE_STATES.has(po.state));
    } else if (stateFilter !== 'ALL') {
      filtered = filtered.filter((po) => po.state === stateFilter);
    }
    return sortBy(filtered, sort, poSortAccessor);
  }, [pos, query, stateFilter, sort]);

  const onSort = (key: PoSortKey) => setSort((prev) => toggleSort(prev, key));

  const onExportCsv = useCallback(() => {
    exportPurchaseOrdersToCsv(visible, opcoRef);
  }, [visible, opcoRef]);

  const receivableCount = pos.filter((p) => RECEIVABLE_STATES.has(p.state)).length;

  return (
    <section className="vendors-screen">
      <div className="screen-header">
        <div>
          <h2>Purchase orders</h2>
          <p className="screen-header-desc">
            {pos.length} {pos.length === 1 ? 'PO' : 'POs'}
            {receivableCount > 0 && ` · ${receivableCount} receivable`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            icon="download"
            onClick={onExportCsv}
            disabled={visible.length === 0}
            title={
              visible.length === 0
                ? 'No rows to export'
                : `Export ${visible.length} row(s) matching the current filter`
            }
          >
            Export CSV
          </Button>
          <Button
            variant="primary"
            icon="plus"
            onClick={() => setDialog({ open: true })}
          >
            New purchase order
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-neg" role="alert">
          Couldn't load purchase orders: {loadError}
        </div>
      )}

      {loading ? (
        <div className="t-body-sm">Loading…</div>
      ) : pos.length === 0 ? (
        <div className="empty-state">
          <p>No purchase orders in {opcoRef} yet. Create one to get started.</p>
        </div>
      ) : (
        <>
          <div className="list-toolbar">
            <FilterInput
              value={query}
              onChange={setQuery}
              placeholder="Search by PO, vendor, or state…"
              ariaLabel="Search purchase orders"
            />
            <Select
              value={stateFilter}
              onValueChange={(v) =>
                setStateFilter(v as PurchaseOrderState | 'ALL' | 'RECEIVABLE')
              }
              aria-label="State filter"
              options={PO_STATE_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            />
            <span className="list-count t-body-sm">
              {visible.length} of {pos.length}
            </span>
          </div>

          {visible.length === 0 ? (
            <div className="empty-state">
              <p>No purchase orders match "{query}".</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <PoSortHeader sort={sort} columnKey="vendor" onSort={onSort}>
                    Vendor
                  </PoSortHeader>
                  <PoSortHeader sort={sort} columnKey="state" onSort={onSort}>
                    State
                  </PoSortHeader>
                  <PoSortHeader sort={sort} columnKey="createdAt" onSort={onSort}>
                    Created
                  </PoSortHeader>
                  <PoSortHeader sort={sort} columnKey="subtotal" onSort={onSort}>
                    Subtotal
                  </PoSortHeader>
                  <PoSortHeader sort={sort} columnKey="lines" onSort={onSort}>
                    Lines
                  </PoSortHeader>
                </tr>
              </thead>
              <tbody>
                {visible.map((po) => (
                  <tr
                    key={po.poRef}
                    className="row-clickable"
                    onClick={() => navigate(`/purchase-orders/${po.poRef}`)}
                  >
                    <td className="cell-primary">{po.vendor?.legalName ?? '—'}</td>
                    <td>
                      <Badge variant={stateBadgeVariant(po.state)} dot={false}>
                        {po.state}
                      </Badge>
                    </td>
                    <td>{formatRelativeDate(po.createdAt)}</td>
                    <td className="mono">
                      {po.subtotal} {po.currencyCode}
                    </td>
                    <td>{po.lines.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {dialog.open && (
        <NewPurchaseOrderDialog
          opcoRef={opcoRef}
          onClose={() => setDialog({ open: false })}
          onCreated={(poRef) => {
            setDialog({ open: false });
            toast.push(`Purchase order created (${poRef})`, 'success');
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

interface PoSortHeaderProps {
  readonly sort: SortState<PoSortKey> | null;
  readonly columnKey: PoSortKey;
  readonly onSort: (key: PoSortKey) => void;
  readonly children: JSX.Element | string;
}

function PoSortHeader({ sort, columnKey, onSort, children }: PoSortHeaderProps): JSX.Element {
  const active = sort?.key === columnKey;
  const indicator = active ? (sort?.direction === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th>
      <button
        type="button"
        className={`th-sort${active ? ' th-sort-active' : ''}`}
        onClick={() => onSort(columnKey)}
      >
        {children}
        <span className="th-sort-indicator" aria-hidden>
          {indicator}
        </span>
      </button>
    </th>
  );
}

function exportPurchaseOrdersToCsv(
  rows: ReadonlyArray<PurchaseOrderRow>,
  opcoRef: string,
): void {
  const header = [
    'poRef',
    'vendorRef',
    'vendorLegalName',
    'opcoRef',
    'state',
    'currencyCode',
    'subtotal',
    'grandTotal',
    'lineCount',
    'orderedQty',
    'receivedQty',
    'createdAt',
    'createdBy',
    'notes',
  ];
  const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = rows.map((po) => {
    const orderedQty = po.lines.reduce((sum, l) => sum + Number(l.quantity), 0);
    const receivedQty = po.lines.reduce(
      (sum, l) => sum + Number(l.receivedToDate ?? 0),
      0,
    );
    return [
      po.poRef,
      po.vendorRef,
      po.vendor?.legalName ?? '',
      po.opcoRef,
      po.state,
      po.currencyCode,
      po.subtotal,
      po.grandTotal,
      String(po.lines.length),
      orderedQty.toFixed(4),
      receivedQty.toFixed(4),
      po.createdAt,
      po.createdBy,
      po.notes ?? '',
    ]
      .map(escape)
      .join(',');
  });
  const blob = new Blob([[header.join(','), ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `anvil-purchase-orders-${opcoRef}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function stateBadgeVariant(state: PurchaseOrderState): BadgeVariant {
  switch (state) {
    case 'OPEN':
    case 'FULLY_RECEIVED':
      return 'success';
    case 'DRAFT':
    case 'APPROVED':
    case 'TRANSMITTED':
    case 'ACKNOWLEDGED':
    case 'PARTIALLY_RECEIVED':
      return 'info';
    case 'CLOSED':
      return 'neutral';
    case 'CANCELLED':
      return 'warning';
  }
}

// ---------------------------------------------------------------------------
// New PO dialog
// ---------------------------------------------------------------------------

interface NewPurchaseOrderDialogProps {
  readonly opcoRef: string;
  readonly onClose: () => void;
  readonly onCreated: (poRef: string) => void;
  readonly onError: (message: string) => void;
}

interface DraftLine {
  readonly id: string;
  description: string;
  unitOfMeasure: string;
  quantity: string;
  unitPrice: string;
  defaultGLAccount: string;
  pricingSource: PricingSource;
}

function emptyLine(): DraftLine {
  return {
    id: crypto.randomUUID(),
    description: '',
    unitOfMeasure: 'EA',
    quantity: '1',
    unitPrice: '0',
    defaultGLAccount: '6100-MRO',
    pricingSource: 'NEGOTIATED',
  };
}

function NewPurchaseOrderDialog({
  opcoRef,
  onClose,
  onCreated,
  onError,
}: NewPurchaseOrderDialogProps): JSX.Element {
  const [vendors, setVendors] = useState<ReadonlyArray<VendorRow>>([]);
  const [vendorLoadError, setVendorLoadError] = useState<string | null>(null);
  const [vendorRef, setVendorRef] = useState<string>('');
  const [currencyCode, setCurrencyCode] = useState<string>('USD');
  const [notes, setNotes] = useState<string>('');
  const [lines, setLines] = useState<ReadonlyArray<DraftLine>>([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchVendors(opcoRef);
        if (cancelled) return;
        const active = rows.filter((v) => v.state === 'ACTIVE');
        setVendors(active);
        if (active.length > 0 && active[0]) {
          setVendorRef(active[0].vendorRef);
        }
      } catch (err) {
        if (cancelled) return;
        setVendorLoadError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opcoRef]);

  const updateLine = (id: string, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (id: string) =>
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((l) => l.id !== id)));

  const subtotal = lines.reduce((sum, l) => {
    const q = Number(l.quantity);
    const p = Number(l.unitPrice);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return sum;
    return sum + q * p;
  }, 0);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!vendorRef) {
      setError('Select a vendor');
      setSubmitting(false);
      return;
    }

    const apiLines: CreatePurchaseOrderLineInput[] = lines.map((l) => ({
      description: l.description.trim(),
      unitOfMeasure: l.unitOfMeasure.trim(),
      quantity: l.quantity.trim(),
      unitPrice: l.unitPrice.trim(),
      defaultGLAccount: l.defaultGLAccount.trim(),
      pricingSource: l.pricingSource,
    }));

    const input: CreatePurchaseOrderInput = {
      vendorRef,
      opcoRef,
      currencyCode: currencyCode.trim().toUpperCase(),
      lines: apiLines,
      notes: notes.trim() === '' ? undefined : notes.trim(),
      requestingUser: REQUESTING_USER,
    };

    try {
      const result = await createPurchaseOrder(input);
      if (result.__typename === 'CreatePurchaseOrderFailure') {
        const msg = `${result.class}: ${JSON.stringify(result.details)}`;
        setError(msg);
        onError(msg);
        setSubmitting(false);
        return;
      }
      onCreated(result.poRef);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open
      title="New purchase order"
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting && vendors.length > 0}
      saveLabel={submitting ? 'Creating…' : 'Create purchase order'}
    >
      <form ref={formRef} onSubmit={onSubmit}>
          {vendorLoadError && (
            <div className="alert alert-neg" role="alert">
              Couldn't load vendors: {vendorLoadError}
            </div>
          )}

          <div className="form-grid">
            <label className="form-row">
              <span className="form-label">Vendor (active only)</span>
              <select
                value={vendorRef}
                onChange={(e) => setVendorRef(e.target.value)}
                required
                disabled={vendors.length === 0}
              >
                {vendors.length === 0 && <option value="">No active vendors</option>}
                {vendors.map((v) => (
                  <option key={v.vendorRef} value={v.vendorRef}>
                    {v.legalName}
                  </option>
                ))}
              </select>
            </label>

            <label className="form-row">
              <span className="form-label">Currency (ISO 4217)</span>
              <input
                type="text"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
                required
                maxLength={3}
                placeholder="USD"
              />
            </label>

            <label className="form-row form-row-full">
              <span className="form-label">Notes (optional)</span>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes"
              />
            </label>
          </div>

          <div className="po-lines">
            <div className="po-lines-header">
              <span className="form-label">Lines</span>
              <button type="button" className="btn btn-sm" onClick={addLine}>
                + Add line
              </button>
            </div>
            <table className="po-lines-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th>UoM</th>
                  <th>Qty</th>
                  <th>Unit price</th>
                  <th>GL</th>
                  <th>Source</th>
                  <th>Line total</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const q = Number(l.quantity);
                  const p = Number(l.unitPrice);
                  const total =
                    Number.isFinite(q) && Number.isFinite(p) ? (q * p).toFixed(4) : '—';
                  return (
                    <tr key={l.id}>
                      <td className="mono">{idx + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={l.description}
                          onChange={(e) => updateLine(l.id, { description: e.target.value })}
                          required
                          placeholder="Widget"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={l.unitOfMeasure}
                          onChange={(e) => updateLine(l.id, { unitOfMeasure: e.target.value })}
                          required
                          className="po-line-uom"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={l.quantity}
                          onChange={(e) => updateLine(l.id, { quantity: e.target.value })}
                          required
                          className="po-line-num"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={l.unitPrice}
                          onChange={(e) => updateLine(l.id, { unitPrice: e.target.value })}
                          required
                          className="po-line-num"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={l.defaultGLAccount}
                          onChange={(e) =>
                            updateLine(l.id, { defaultGLAccount: e.target.value })
                          }
                          required
                          className="po-line-gl"
                        />
                      </td>
                      <td>
                        <select
                          value={l.pricingSource}
                          onChange={(e) =>
                            updateLine(l.id, { pricingSource: e.target.value as PricingSource })
                          }
                        >
                          {PRICING_SOURCES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="mono">{total}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => removeLine(l.id)}
                          aria-label="Remove line"
                          disabled={lines.length === 1}
                          title={lines.length === 1 ? 'At least one line is required' : 'Remove line'}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={7} className="po-lines-foot-label">
                    Subtotal
                  </td>
                  <td className="mono">{subtotal.toFixed(4)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {error && (
            <div className="alert alert-neg" role="alert">
              {error}
            </div>
          )}

      </form>
    </SidePanel>
  );
}
